import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Home, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | success | timeout | error
  const [amount, setAmount] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) {
      setState('error');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      const { payment_status, amount: amt } = res.data;
      if (amt != null) setAmount(amt);
      if (payment_status === 'paid') {
        setState('success');
        return;
      }
      if (payment_status === 'expired' || payment_status === 'failed') {
        setState('error');
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState('timeout');
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
    } catch (err) {
      console.error('Status poll failed:', err);
      if (attempt >= MAX_POLLS) {
        setState('error');
      } else {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    poll(0);
  }, [poll]);

  const iconMap = {
    checking: <Loader2 size={64} color="#00d4ff" className="spin-anim" />,
    success: <CheckCircle2 size={64} color="#22c55e" />,
    timeout: <Loader2 size={64} color="#f59e0b" />,
    error: <XCircle size={64} color="#ef4444" />,
  };

  const content = {
    checking: { title: 'Confirming your donation…', msg: 'Please wait while we verify your payment. This only takes a moment.' },
    success: { title: 'Thank You for Your Generosity!', msg: `Your${amount ? ` $${Number(amount).toLocaleString()}` : ''} donation has been received. Your support helps spread consciousness and wisdom to seekers everywhere.` },
    timeout: { title: 'Still Processing', msg: 'Your payment is taking a little longer than usual to confirm. If you completed checkout, it will be processed shortly.' },
    error: { title: 'Payment Not Confirmed', msg: 'We could not confirm your payment. If you were charged, please contact us and we will look into it.' },
  };

  const c = content[state];

  return (
    <div style={{ background: '#0f0a1e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="glass-card" data-testid="payment-success-card" style={{ maxWidth: 520, width: '100%', padding: 'clamp(32px, 6vw, 56px)', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          {iconMap[state]}
        </div>
        {state === 'success' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <Heart size={20} fill="#ef4444" color="#ef4444" />
          </div>
        )}
        <h1 style={{ fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700, color: '#ffffff', marginBottom: 14 }}>
          {c.title}
        </h1>
        <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 32 }}>
          {c.msg}
        </p>
        <button
          className="btn-primary-gradient"
          data-testid="payment-success-home-btn"
          onClick={() => navigate('/')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
        >
          <Home size={18} /> Back to Home
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
